import { keepPreviousData, useQuery, type UseQueryOptions } from '@tanstack/vue-query';
import type { Ref } from 'vue';
import type { ApprovedFeedDeliveryApiRow, ApprovedFeedDeliveryRow, FeedDeliveryWeeklyRow, OriginalFeedDeliveryApiRow, OriginalFeedDeliveryRow, RecordsListFetchParams } from '@/types';
import { mapApprovedFeedDeliveryRow, mapOriginalFeedDeliveryRow } from '@/types';
import ApiRequest from '@/plugins/store/actions/api/ApiRequest';

const DEFAULT_OPTIONS = {
    staleTime: 1000 * 60 * 5, // 5 minutes
    gcTime: 1000 * 60 * 5, // 5 minutes
};

/** The period and location scope every endpoint below takes, as its query string. */
function scopeQuery(params: RecordsListFetchParams): string {
    return `${params.period}&location_ids=${params.locationIds.join(',')}`;
}

export const queryKeys = {
    getFeedDeliveriesWeeklyRoot: ['feed-deliveries', 'weekly'],
    feedDeliveriesWeeklyWithParams: (params: Ref<RecordsListFetchParams>) => [...queryKeys.getFeedDeliveriesWeeklyRoot, params],
    getFeedDeliveriesApprovedRoot: ['feed-deliveries', 'approved'],
    feedDeliveriesApprovedWithParams: (params: Ref<RecordsListFetchParams>) => [...queryKeys.getFeedDeliveriesApprovedRoot, params],
    getFeedDeliveriesOriginalRoot: ['feed-deliveries', 'original'],
    feedDeliveriesOriginalWithParams: (params: Ref<RecordsListFetchParams>) => [...queryKeys.getFeedDeliveriesOriginalRoot, params],
};

/**
 * Every (week, Feed Mix) delivery total in the selected period and location scope, unpaged. Feeds
 * the chart only - `useGroupedWeeklyAggregation` bins and ranks it client-side, the same way the
 * pig-domain records pages bin `RecordRow[]`.
 */
export function useFeedDeliveriesWeekly(fetchParams: Ref<RecordsListFetchParams>, options: UseQueryOptions<FeedDeliveryWeeklyRow[]> = {}) {
    return useQuery<FeedDeliveryWeeklyRow[]>({
        queryKey: queryKeys.feedDeliveriesWeeklyWithParams(fetchParams),
        queryFn: () => new ApiRequest({ skipLoadingIndicator: true }).send('get', `records/feed-deliveries/weekly?${scopeQuery(fetchParams.value)}`),
        placeholderData: keepPreviousData,
        ...DEFAULT_OPTIONS,
        ...options,
    });
}

/**
 * Every system-registered delivery in the selected period and location scope, unpaged. The wire row
 * is reshaped to `ApprovedFeedDeliveryRow` here rather than asserted directly - see
 * `mapApprovedFeedDeliveryRow` for why, same reason `useDeaths` reshapes `DeathApiRow`.
 */
export function useFeedDeliveriesApproved(fetchParams: Ref<RecordsListFetchParams>, options: UseQueryOptions<ApprovedFeedDeliveryRow[]> = {}) {
    return useQuery<ApprovedFeedDeliveryRow[]>({
        queryKey: queryKeys.feedDeliveriesApprovedWithParams(fetchParams),
        queryFn: () =>
            new ApiRequest({ skipLoadingIndicator: true })
                .send('get', `records/feed-deliveries/approved?${scopeQuery(fetchParams.value)}`)
                .then((rows: ApprovedFeedDeliveryApiRow[]) => rows.map(mapApprovedFeedDeliveryRow)),
        placeholderData: keepPreviousData,
        ...DEFAULT_OPTIONS,
        ...options,
    });
}

/**
 * Every raw supplier-reported delivery in the selected period and location scope, unpaged. The wire
 * row is reshaped to `OriginalFeedDeliveryRow` here for the same reason as `useFeedDeliveriesApproved`.
 */
export function useFeedDeliveriesOriginal(fetchParams: Ref<RecordsListFetchParams>, options: UseQueryOptions<OriginalFeedDeliveryRow[]> = {}) {
    return useQuery<OriginalFeedDeliveryRow[]>({
        queryKey: queryKeys.feedDeliveriesOriginalWithParams(fetchParams),
        queryFn: () =>
            new ApiRequest({ skipLoadingIndicator: true })
                .send('get', `records/feed-deliveries/original?${scopeQuery(fetchParams.value)}`)
                .then((rows: OriginalFeedDeliveryApiRow[]) => rows.map(mapOriginalFeedDeliveryRow)),
        placeholderData: keepPreviousData,
        ...DEFAULT_OPTIONS,
        ...options,
    });
}
